import { Lead } from '../types';
import { EnrichmentMetadata as APIEnrichmentMetadata } from '../types/table';
import { apiClient } from '../lib/api';
import * as XLSX from 'xlsx';

// Column types for export classification
type ColumnType = 'base' | 'segment' | 'enrichment';

interface ExportColumn {
  key: string;
  name: string;
  type: ColumnType;
  width?: number;
  isSegment?: boolean;
}

interface EnrichmentMetadata {
  summary?: string;
  sourceUrl?: string;
  confidenceScore?: number;
  dataSources?: any[];
}

interface SegmentReasoning {
  satisfied?: string;
  reasoningText?: string;
  dataSources?: any[];
  criterionText?: string;
}

interface ExportOptions {
  format: 'csv' | 'xlsx';
  filename: string;
  includeReasoning?: boolean;
  includeSources?: boolean;
}

// Base columns that are fundamental to each table type
const TABLE_TYPE_BASE_COLUMNS = {
  companies: ['Name', 'Company Name', 'Description', 'URL', 'Website'],
  people: ['Name', 'Full Name', 'Company', 'Job Title', 'Email', 'URL'],
  custom: ['Name', 'Description', 'URL']
};

/**
 * Column Classification Engine
 * Analyzes table columns and categorizes them for export
 */
class ColumnClassifier {
  private baseColumnNames: Set<string>;

  constructor() {
    // Create a comprehensive set of base column names
    const allBaseColumns = [
      ...TABLE_TYPE_BASE_COLUMNS.companies,
      ...TABLE_TYPE_BASE_COLUMNS.people,
      ...TABLE_TYPE_BASE_COLUMNS.custom
    ];
    this.baseColumnNames = new Set(allBaseColumns);
  }

  /**
   * Classify a column based on its properties
   */
  classifyColumn(column: { key: string; name: string; isSegment?: boolean }): ExportColumn {
    if (column.isSegment) {
      return {
        key: column.key,
        name: column.name,
        type: 'segment'
      };
    }

    if (this.baseColumnNames.has(column.name)) {
      return {
        key: column.key,
        name: column.name,
        type: 'base'
      };
    }

    // Everything else is considered an enrichment column
    return {
      key: column.key,
      name: column.name,
      type: 'enrichment'
    };
  }

  /**
   * Process an array of columns and return classified export columns
   */
  processColumns(columns: { key: string; name: string; isSegment?: boolean; width?: number }[]): ExportColumn[] {
    return columns.map(col => ({
      ...this.classifyColumn(col),
      width: col.width
    }));
  }
}

/**
 * Data Extraction Engine
 * Extracts cell values and metadata from leads using table-consistent logic
 */
class DataExtractor {
  private enrichmentCache: Record<string, Record<string, APIEnrichmentMetadata>> = {};

  /**
   * Fetch enrichment metadata for all leads in a table
   */
  async fetchEnrichmentMetadata(tableId: string, rowIds: string[]): Promise<void> {
    try {
      this.enrichmentCache = await apiClient.getEnrichmentMetadata(tableId, rowIds);
    } catch (error) {
      console.error('Failed to fetch enrichment metadata:', error);
      this.enrichmentCache = {};
    }
  }

  /**
   * Get cell value using the same logic as ProfessionalTable
   */
  getCellValue(lead: Lead, column: ExportColumn): string {
    // Handle segment columns
    if (column.type === 'segment') {
      return this.getSegmentValue(lead, column.key);
    }

    // Handle name column specifically
    if (column.key === 'name' || column.name === 'Name' || column.name === 'Full Name') {
      return lead.name || lead.data?.name || lead.data?.Name || lead.data?.company_name || 'Unnamed';
    }

    // Try to find the value using both the column key and the original column name
    if (column.name && lead.data?.[column.name] !== undefined) {
      return lead.data[column.name];
    }

    // Fallback to the column key
    return lead.data?.[column.key] || lead.data?.[column.key.replace(/_\d+$/, '')] || '';
  }

  /**
   * Get segment value with V1/V2 compatibility
   */
  private getSegmentValue(lead: Lead, columnKey: string): string {
    const segmentId = columnKey.replace('segment_', '');
    const reasoning = lead.reasoning;

    if (!reasoning) {
      return 'Unknown';
    }

    let segmentReasoning = reasoning[segmentId];

    // If not found by segment ID, try to find by criterion text for virtual segments
    if (!segmentReasoning && segmentId.startsWith('virtual_')) {
      segmentReasoning = Object.values(reasoning).find((r: any) =>
        r.criterion_text && columnKey.includes(r.criterion_text.replace(/[^a-zA-Z0-9]/g, '_'))
      );
    }

    if (!segmentReasoning) {
      return 'Unknown';
    }

    const satisfied = segmentReasoning.satisfied;

    // Map V2 satisfied values to display text
    if (satisfied === 'yes') return 'Match';
    if (satisfied === 'no') return 'Miss';
    if (satisfied === 'unclear') return 'Unclear';
    return 'Unknown';
  }

  /**
   * Extract enrichment metadata for a column from the cache
   */
  getEnrichmentMetadata(lead: Lead, columnName: string): EnrichmentMetadata {
    const rowMetadata = this.enrichmentCache[lead.id];
    if (!rowMetadata || !rowMetadata[columnName]) {
      return {
        summary: undefined,
        sourceUrl: undefined,
        confidenceScore: undefined,
        dataSources: []
      };
    }

    const metadata = rowMetadata[columnName];
    return {
      summary: metadata.summary || undefined,
      sourceUrl: metadata.sourceUrl || undefined,
      confidenceScore: metadata.confidenceScore || undefined,
      dataSources: metadata.dataSources || []
    };
  }

  /**
   * Extract segment reasoning for a segment column
   */
  getSegmentReasoning(lead: Lead, column: ExportColumn): SegmentReasoning {
    if (column.type !== 'segment') return {};

    const segmentId = column.key.replace('segment_', '');
    const reasoning = lead.reasoning;

    if (!reasoning) return {};

    let segmentReasoning = reasoning[segmentId];

    // Handle virtual segments
    if (!segmentReasoning && segmentId.startsWith('virtual_')) {
      segmentReasoning = Object.values(reasoning).find((r: any) =>
        r.criterion_text && column.key.includes(r.criterion_text.replace(/[^a-zA-Z0-9]/g, '_'))
      );
    }

    if (!segmentReasoning) return {};

    return {
      satisfied: segmentReasoning.satisfied,
      reasoningText: segmentReasoning.reasoning_text,
      dataSources: segmentReasoning.source_urls || segmentReasoning.data_sources || [],
      criterionText: segmentReasoning.criterion_text
    };
  }

  /**
   * Check if any lead has enrichment data for a column
   */
  hasEnrichmentData(leads: Lead[], columnName: string): boolean {
    return leads.some(lead => {
      const metadata = this.getEnrichmentMetadata(lead, columnName);
      return metadata.summary || metadata.sourceUrl || (metadata.dataSources && metadata.dataSources.length > 0);
    });
  }

  /**
   * Check if any lead has segment reasoning for a column
   */
  hasSegmentReasoning(leads: Lead[], column: ExportColumn): boolean {
    if (column.type !== 'segment') return false;

    return leads.some(lead => {
      const reasoning = this.getSegmentReasoning(lead, column);
      return reasoning.reasoningText && reasoning.reasoningText.trim() !== '';
    });
  }
}

/**
 * Format Adapters
 * Handle format-specific output generation
 */
class CSVAdapter {
  /**
   * Escape and format a value for CSV
   */
  private formatValue(value: any): string {
    let stringValue = String(value || '');

    // Handle arrays
    if (Array.isArray(value)) {
      stringValue = value.join('; ');
    }

    // Escape quotes and handle special characters
    stringValue = stringValue.replace(/"/g, '""');
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
      stringValue = `"${stringValue}"`;
    }

    return stringValue;
  }

  /**
   * Generate CSV content from structured data
   */
  generate(headers: string[], rows: string[][]): string {
    const csvRows = [
      headers.map(h => this.formatValue(h)).join(','),
      ...rows.map(row => row.map(cell => this.formatValue(cell)).join(','))
    ];

    return csvRows.join('\n');
  }
}

class ExcelAdapter {
  /**
   * Format sources for display
   */
  private formatSources(sources: any[]): string {
    if (!Array.isArray(sources) || sources.length === 0) return '';

    return sources.map(source => {
      if (typeof source === 'string') {
        try {
          const parsed = JSON.parse(source);
          return parsed.url || source;
        } catch {
          return source;
        }
      }
      return source.url || String(source);
    }).join('; ');
  }

  /**
   * Generate Excel workbook from structured data
   */
  generate(headers: string[], rows: any[][]): Uint8Array {
    // Convert rows to objects for XLSX
    const data = rows.map(row => {
      const obj: { [key: string]: any } = {};
      headers.forEach((header, index) => {
        let value = row[index];

        // Handle arrays for Excel
        if (Array.isArray(value)) {
          value = this.formatSources(value);
        }

        obj[header] = String(value || '');
      });
      return obj;
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Export Data');

    // Auto-size columns
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    const colWidths: { wch: number }[] = [];

    for (let col = range.s.c; col <= range.e.c; col++) {
      let maxWidth = 10; // minimum width

      // Check header width
      const header = headers[col];
      if (header) {
        maxWidth = Math.max(maxWidth, header.length);
      }

      // Check data width (sample first few rows for performance)
      const sampleSize = Math.min(data.length, 20);
      for (let row = 0; row < sampleSize; row++) {
        const cellValue = String((data[row] && Object.values(data[row])[col]) || '');
        maxWidth = Math.max(maxWidth, cellValue.length);
      }

      // Cap at reasonable maximum
      colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    }

    ws['!cols'] = colWidths;

    // Convert to binary data
    return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  }
}

/**
 * Export Orchestrator
 * Coordinates the entire export process
 */
class ExportOrchestrator {
  private classifier: ColumnClassifier;
  private extractor: DataExtractor;
  private csvAdapter: CSVAdapter;
  private excelAdapter: ExcelAdapter;

  constructor() {
    this.classifier = new ColumnClassifier();
    this.extractor = new DataExtractor();
    this.csvAdapter = new CSVAdapter();
    this.excelAdapter = new ExcelAdapter();
  }

  /**
   * Build export columns with reasoning and enrichment metadata
   */
  private buildExportStructure(
    columns: { key: string; name: string; isSegment?: boolean; width?: number }[],
    leads: Lead[],
    options: ExportOptions
  ): { headers: string[], columns: ExportColumn[] } {
    const exportColumns = this.classifier.processColumns(columns);
    const headers: string[] = [];
    const finalColumns: ExportColumn[] = [];

    for (const column of exportColumns) {
      // Add the main column
      headers.push(column.name);
      finalColumns.push(column);

      // Add enrichment metadata for enrichment columns
      if (column.type === 'enrichment' && this.extractor.hasEnrichmentData(leads, column.name)) {
        if (options.includeReasoning !== false) {
          headers.push(`${column.name} - Summary`);
          finalColumns.push({ ...column, key: `${column.key}_summary` });
        }

        if (options.includeSources !== false) {
          headers.push(`${column.name} - Sources`);
          finalColumns.push({ ...column, key: `${column.key}_sources` });
        }
      }

      // Add reasoning for segment columns
      if (column.type === 'segment' && this.extractor.hasSegmentReasoning(leads, column)) {
        if (options.includeReasoning !== false) {
          headers.push(`${column.name} - Reasoning`);
          finalColumns.push({ ...column, key: `${column.key}_reasoning` });
        }

        if (options.includeSources !== false) {
          headers.push(`${column.name} - Sources`);
          finalColumns.push({ ...column, key: `${column.key}_sources` });
        }
      }
    }

    return { headers, columns: finalColumns };
  }

  /**
   * Extract data for all leads and columns
   */
  private extractData(leads: Lead[], exportColumns: ExportColumn[]): any[][] {
    return leads.map(lead => {
      return exportColumns.map(column => {
        // Handle special metadata columns
        if (column.key.endsWith('_summary')) {
          const originalColumn = column.name.replace(' - Summary', '');
          const metadata = this.extractor.getEnrichmentMetadata(lead, originalColumn);
          return metadata.summary || '';
        }

        if (column.key.endsWith('_sources')) {
          const originalColumn = column.name.replace(' - Sources', '');

          if (column.type === 'segment') {
            const reasoning = this.extractor.getSegmentReasoning(lead, {
              ...column,
              key: column.key.replace('_sources', '')
            });
            return reasoning.dataSources || [];
          } else {
            const metadata = this.extractor.getEnrichmentMetadata(lead, originalColumn);
            return metadata.dataSources || [];
          }
        }

        if (column.key.endsWith('_reasoning')) {
          const reasoning = this.extractor.getSegmentReasoning(lead, {
            ...column,
            key: column.key.replace('_reasoning', '')
          });
          return reasoning.reasoningText || '';
        }

        // Regular cell value
        return this.extractor.getCellValue(lead, column);
      });
    });
  }

  /**
   * Main export function (now async to fetch enrichment metadata)
   */
  async export(
    leads: Lead[],
    columns: { key: string; name: string; isSegment?: boolean; width?: number }[],
    options: ExportOptions & { tableId?: string }
  ): Promise<void> {
    if (leads.length === 0) {
      throw new Error('No data to export');
    }

    // Build export structure
    const { headers, columns: exportColumns } = this.buildExportStructure(columns, leads, options);

    // Fetch enrichment metadata if needed and tableId is provided
    const hasEnrichmentColumns = exportColumns.some(col => col.type === 'enrichment');
    if (hasEnrichmentColumns && options.tableId) {
      const rowIds = leads.map(lead => lead.id);
      await this.extractor.fetchEnrichmentMetadata(options.tableId, rowIds);
    }

    // Extract data
    const rows = this.extractData(leads, exportColumns);

    // Generate output based on format
    if (options.format === 'csv') {
      const csvContent = this.csvAdapter.generate(headers, rows);
      this.downloadCSV(csvContent, options.filename);
    } else {
      const excelData = this.excelAdapter.generate(headers, rows);
      this.downloadExcel(excelData, options.filename);
    }
  }

  /**
   * Download CSV file
   */
  private downloadCSV(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Download Excel file
   */
  private downloadExcel(data: Uint8Array, filename: string): void {
    // Uint8Array is a valid BufferSource/BlobPart, but TypeScript's strict checking
    // complains about the generic ArrayBufferLike type. Create a new Uint8Array
    // with explicit ArrayBuffer type to satisfy TypeScript.
    const uint8Array = new Uint8Array(data);
    const blob = new Blob([uint8Array], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

// Public API Functions (maintaining backward compatibility)

/**
 * Export table data to CSV
 */
export const exportTableToCSV = async (
  leads: Lead[],
  columns: { key: string; name: string; isSegment?: boolean; width?: number }[],
  filename?: string,
  tableId?: string
): Promise<void> => {
  const orchestrator = new ExportOrchestrator();
  const finalFilename = filename || `signaliq-data-${new Date().toISOString().split('T')[0]}.csv`;

  await orchestrator.export(leads, columns, {
    format: 'csv',
    filename: finalFilename,
    includeReasoning: true,
    includeSources: true,
    tableId
  });
};

/**
 * Export table data to Excel
 */
export const exportTableToExcel = async (
  leads: Lead[],
  columns: { key: string; name: string; isSegment?: boolean; width?: number }[],
  filename?: string,
  tableId?: string
): Promise<void> => {
  const orchestrator = new ExportOrchestrator();
  const finalFilename = filename || `signaliq-data-${new Date().toISOString().split('T')[0]}.xlsx`;

  await orchestrator.export(leads, columns, {
    format: 'xlsx',
    filename: finalFilename,
    includeReasoning: true,
    includeSources: true,
    tableId
  });
};

/**
 * Legacy function for backward compatibility
 */
export const exportLeadsToCSV = async (leads: Lead[], filename?: string): Promise<void> => {
  // Convert legacy format to new format
  const columns = [
    { key: 'name', name: 'Name' },
    { key: 'type', name: 'Type' }
  ];

  // Add all unique data keys as columns
  const dataKeys = new Set<string>();
  leads.forEach(lead => {
    Object.keys(lead.data || {}).forEach(key => dataKeys.add(key));
  });

  dataKeys.forEach(key => {
    columns.push({
      key,
      name: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
    });
  });

  const finalFilename = filename || `signaliq-data-${new Date().toISOString().split('T')[0]}.csv`;
  await exportTableToCSV(leads, columns, finalFilename);
};
/**
 * Generic CSV download from headers + rows. Values are stringified and quoted
 * when they contain a comma, quote, or newline (RFC 4180 escaping).
 */
export const downloadRowsAsCsv = (
  filename: string,
  headers: string[],
  rows: (string | number | null | undefined)[][]
): void => {
  const esc = (v: string | number | null | undefined): string => {
    const s = v == null ? '' : String(v);
    // Guard CSV formula injection: a leading =,+,-,@ (or tab/CR) executes as a
    // formula in Excel/Sheets, so prefix a single quote before RFC-4180 quoting.
    const g = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
    return /[",\n\r]/.test(g) ? `"${g.replace(/"/g, '""')}"` : g;
  };
  const content = [headers, ...rows].map(row => row.map(esc).join(',')).join('\r\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
